import { useState } from 'react';
import { useUIStore, useAuthStore } from '../store';
import { CalendarSubscriptionModal } from './CalendarSubscriptionModal';
import type { ViewType } from '../types';
import {
  List,
  Calendar,
  Grid3X3,
  GitBranch,
  FolderKanban,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Link2,
} from 'lucide-react';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const menuItems: { id: ViewType; label: string; icon: React.ElementType }[] = [
  { id: 'list', label: '列表视图', icon: List },
  { id: 'week', label: '周视图', icon: Calendar },
  { id: 'month', label: '月视图', icon: Grid3X3 },
  { id: 'mindmap', label: '思维导图', icon: GitBranch },
  { id: 'project', label: '项目视图', icon: FolderKanban },
  { id: 'stats', label: '统计面板', icon: BarChart3 },
];

export function Sidebar({ open, onClose }: SidebarProps) {
  const { currentView, setCurrentView } = useUIStore();
  const { user, logout } = useAuthStore();
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  return (
    <>
      {/* 移动端遮罩 */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* 侧边栏 */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transition-all duration-300 ${
          open ? 'w-64 translate-x-0' : 'w-64 -translate-x-full'
        }`}
      >
        {/* Logo区域 */}
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Life OS</h1>
              <p className="text-xs text-gray-500">个人生活管理</p>
            </div>
          </div>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-3 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleViewChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                  {item.label}
                  {isActive && (
                    <ChevronRight className="w-4 h-4 ml-auto text-blue-600" />
                  )}
                </button>
              );
            })}
          </div>

          {/* 分隔线 */}
          <div className="my-4 border-t border-gray-200" />

          {/* 其他菜单 */}
          <div className="px-3 space-y-1">
            <button
              onClick={() => setIsCalendarModalOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all"
            >
              <Link2 className="w-5 h-5 text-gray-500" />
              日历订阅
            </button>
            <button
              onClick={() => handleViewChange('list')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all"
            >
              <Trash2 className="w-5 h-5 text-gray-500" />
              回收站
            </button>
            <button
              onClick={() => handleViewChange('list')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all"
            >
              <Settings className="w-5 h-5 text-gray-500" />
              设置
            </button>
          </div>
        </nav>

        {/* 用户信息 */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || '用户'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || ''}
              </p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
              title="退出登录"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* 展开/收起按钮（仅桌面端显示） */}
      <button
        onClick={onClose}
        className={`fixed top-1/2 z-50 hidden md:flex items-center justify-center w-6 h-12 bg-white border border-gray-200 rounded-r-lg shadow-md transition-all duration-300 ${
          open ? 'left-64' : 'left-0'
        }`}
        style={{ transform: 'translateY(-50%)' }}
      >
        {open ? (
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {/* 日历订阅模态框 */}
      <CalendarSubscriptionModal
        isOpen={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
      />
    </>
  );
}
